<template>
  <UiDrawer v-model:aperto="store.aperto" :aria-label="t('layout.comandi.titolo')" larghezza="26rem">
    <template #header>
      <div class="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
        <div class="min-w-0">
          <h2 class="font-display text-lg font-semibold text-text-primary">{{ t("layout.comandi.titolo") }}</h2>
          <p class="mt-0.5 text-sm text-text-secondary">{{ t("layout.comandi.descrizione", { min: dati?.finestra_min ?? 60 }) }}</p>
        </div>
        <UiButton variant="ghost" size="sm" icon="fal fa-xmark" :aria-label="t('layout.comandi.chiudi')" data-test="comandi-chiudi" @click="store.chiudi()" />
      </div>
    </template>

    <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-4" data-test="comandi-corpo">
      <SkeletonRegion v-if="caricamentoIniziale" class="flex flex-col gap-3">
        <SkeletonBlock v-for="n in 3" :key="n" height="4.5rem" radius="xl" />
      </SkeletonRegion>
      <ErrorState v-else-if="errore && !dati" :description="t('layout.comandi.errore')" :retrying="ritentando" @retry="ritenta" />
      <EmptyState v-else-if="!gruppi.length" icon="fal fa-paper-plane" :description="t('layout.comandi.vuoto')" />
      <div v-else class="flex flex-col gap-4">
        <section v-for="gruppo in gruppi" :key="gruppo.idImpianto ?? 'senza'" :aria-label="gruppo.nome" data-test="comandi-gruppo">
          <h3 class="mb-1.5 flex items-center justify-between gap-2 px-1 text-xs font-semibold tracking-wide text-text-accent uppercase">
            <span class="truncate">{{ gruppo.nome }}</span>
            <NuxtLink v-if="gruppo.idImpianto !== null" :to="`/impianti/${gruppo.idImpianto}?scheda=comandi`" class="interactive shrink-0 normal-case underline-offset-2 hover:underline" @click="store.chiudi()">{{ t("layout.comandi.apri_impianto") }}</NuxtLink>
          </h3>
          <ul class="flex flex-col gap-2">
            <LayoutComandiAttiviRiga v-for="riga in gruppo.righe" :key="riga.id" :riga="riga" />
          </ul>
        </section>
        <p v-if="nascosti > 0" class="px-1 text-xs text-text-muted" role="status" data-test="comandi-altri">{{ t("layout.comandi.altri", { n: nascosti }) }}</p>
        <p v-if="errore && dati" class="px-1 text-xs text-text-muted" role="status">{{ t("layout.comandi.non_aggiornato") }}</p>
      </div>
    </div>
  </UiDrawer>
</template>

<script setup lang="ts">
// Pannello «Comandi manuali attivi» (barra laterale, tutti i ruoli): i comandi in corso e quelli dell'ultima ora sugli impianti dell'utente, anche di altri
// utenti (`GET /api/comandi/attivi`). Si legge solo mentre il menu o il pannello sono aperti (ogni 5 s); il conteggio dei comandi in corso va allo store per
// il numero sul pulsante del menu.
import { useI18n } from "vue-i18n";
import { raggruppaPerImpianto } from "~/utils/comandiAttivi";
import type { RispostaComandiAttivi } from "~~/shared/data-model/log_comandi";

const INTERVALLO_MS = 5000;

const { t } = useI18n();
const store = useComandiAttiviStore();
const ui = useUiStore();

// il menu aperto basta: il suo pulsante mostra il numero dei comandi in corso
const attivo = computed(() => store.aperto || ui.menuAperto);
let ultimo: RispostaComandiAttivi | null = null;

const { dati, errore, caricamentoIniziale, ritentando, carica, ritenta } = useRichiesta<RispostaComandiAttivi | null>(
  async ({ authFetch }) => {
    // da chiusi non si chiede nulla: il timer del polling continua a girare ma non fa richieste
    if (!attivo.value) return ultimo;
    const risposta = await authFetch<{ success?: boolean; error?: string; data?: RispostaComandiAttivi }>("/api/comandi/attivi", { method: "GET" });
    // la risposta può portare `error` anche con HTTP 200: si tratta come un errore di lettura
    if (!risposta?.data) throw new Error(risposta?.error ?? "comandi_attivi");
    ultimo = risposta.data;
    return ultimo;
  },
  { immediata: false, intervallo: INTERVALLO_MS },
);

watch(attivo, (acceso) => {
  if (acceso) void carica();
});
watch(dati, (d) => store.impostaInCorso(d?.in_corso ?? 0), { immediate: true });

const gruppi = computed(() => raggruppaPerImpianto(dati.value?.righe ?? []));
const nascosti = computed(() => Math.max(0, (dati.value?.totale ?? 0) - (dati.value?.righe.length ?? 0)));
</script>
